import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import SvgIcon from "@mui/material/SvgIcon";
import { fetchReservationsPage, buildHotelInfoMessage, BOOKING_STATUS_OPTIONS } from "../../chatbot-admin/chatbotApi";
import ReactToPrint from "react-to-print";
import { QUICK_ACTIONS, REPORT_OPTIONS, matchIntent } from "../../chatbot-admin/intents";
import { REPORTS, reportSubtitle } from "../../chatbot-admin/reportRegistry";
import ReportDialog from "../../chatbot-admin/ReportDialog";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "bot",
  kind: "text",
  text: "Hello! How can I help you today?",
};
const FALLBACK_TYPED_MESSAGE = "I can help with Hotel details, Reservations and Reports (try \"Z - Out Report\") — or tap a quick action below.";

const OPEN_MS = 420;
const CLOSE_MS = 280;

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function todayISO() {
  return toLocalISO(new Date());
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalISO(d);
}

/**
 * Manager-only chat widget — same visual language (glow, clip-path
 * reveal, bell-icon launcher, bubble animations) as the guest-facing
 * ChatWidget.js, but fully separate file: no shared imports with
 * chatbot/, so changes here never affect the Direct Booking page's bot.
 *
 * hotelDetails — pass the same raw object the admin panel already has
 * for this hotel (GET /hotel/hotelDetail/{id} shape).
 */
export default function AdminChatWidget({
  hotelDetails,
  hotelName = "Manager Assistant",
  primaryColor = "#0f172a",
  accentColor = "#2563eb",
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [reportDialog, setReportDialog] = useState({ open: false, key: null, params: {}, data: null, loading: false, error: "" });
  const reportReq = useRef(0); // ignore stale responses
  const printRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  // Keep the window mounted long enough for the closing animation to play
  useEffect(() => {
    if (open) {
      setMounted(true);
    } else if (mounted) {
      const t = setTimeout(() => setMounted(false), CLOSE_MS);
      return () => clearTimeout(t);
    }
  }, [open]); 

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  // One router for chips, report-menu options and typed text.
  const runAction = (action) => {
    if (action === "ADMIN_HOTEL_DETAILS") {
      const { text, link } = buildHotelInfoMessage(hotelDetails);
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text, link });
    } else if (action === "ADMIN_RESERVATION") {
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "dateForm" });
    } else if (action === "ADMIN_REPORT_MENU") {
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "reportMenu" });
    } else if (REPORTS[action]) {
      // any registered report: date form (single/range) → dialog; "none" opens directly
      if (REPORTS[action].dateMode === "none") loadReport(action, {});
      else pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "reportDateForm", reportKey: action });
    }
  };

  const handleChipClick = (qa) => runAction(qa.action);

  const handleTypedSend = () => {
    const content = input.trim();
    if (!content) return;
    pushMessage({ id: crypto.randomUUID(), role: "user", kind: "text", text: content });
    setInput("");
    const action = matchIntent(content);
    if (action) runAction(action);
    else pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: FALLBACK_TYPED_MESSAGE });
  };

  // ---- Report dialog -------------------------------------------------
  const loadReport = async (key, params) => {
    const cfg = REPORTS[key];
    if (!cfg || !params) return;
    const reqId = ++reportReq.current;
    // selfLoading view (e.g. the real report page) fetches by itself
    if (cfg.selfLoading) {
      setReportDialog({ open: true, key, params, data: null, loading: false, error: "" });
      return;
    }
    setReportDialog({ open: true, key, params, data: null, loading: true, error: "" });
    try {
      const data = await cfg.fetchData(params);
      if (reqId !== reportReq.current) return;
      setReportDialog((d) => ({ ...d, data, loading: false }));
    } catch (error) {
      console.error("FETCH REPORT ERROR:", error);
      if (reqId !== reportReq.current) return;
      setReportDialog((d) => ({ ...d, loading: false, error: "Couldn't load this report — please try again." }));
    }
  };

  const closeReportDialog = () => {
    reportReq.current++;
    setReportDialog((d) => ({ ...d, open: false }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTypedSend();
    }
  };

  // filters: { startDate, endDate, searchText, searchById, bookingStatus }
  const handleReservationSearch = async (filters) => {
    if (!filters?.startDate || !filters?.endDate || loading) return;
    setLoading(true);
    try {
      const { rows, totalRows } = await fetchReservationsPage({ ...filters, pageNo: 1 });
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "reservationList", ...filters, rows, totalRows, pageNo: 1 });
      if (rows[0]) setExpandedId(rows[0].id);
    } catch (error) {
      console.error("FETCH RESERVATIONS ERROR:", error);
      pushMessage({ id: crypto.randomUUID(), role: "bot", kind: "text", text: "I couldn't load reservations — please try again." });
    } finally {
      setLoading(false);
    }
  };

  // msg already carries the filters it was searched with
  const handleLoadMore = async (msg) => {
    if (loading) return;
    setLoading(true);
    try {
      const nextPage = msg.pageNo + 1;
      const { startDate, endDate, searchText, searchById, bookingStatus } = msg;
      const { rows: newRows, totalRows } = await fetchReservationsPage({
        startDate, endDate, searchText, searchById, bookingStatus, pageNo: nextPage,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, rows: [...m.rows, ...newRows], pageNo: nextPage, totalRows } : m))
      );
    } catch (error) {
      console.error("LOAD MORE RESERVATIONS ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: "fixed", bottom: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 }, zIndex: 1300 }}>
      {/* Reusable report dialog — body/print come from reportRegistry */}
      <ReportDialogHost
        state={reportDialog}
        printRef={printRef}
        onClose={closeReportDialog}
        onRetry={() => loadReport(reportDialog.key, reportDialog.params)}
        primaryColor={primaryColor}
        accentColor={accentColor}
      />

      {/* Ambient glow behind the panel while open */}
      {mounted && (
        <Box
          sx={{
            position: "absolute",
            bottom: 76,
            left: 0,
            width: { xs: "92vw", sm: 420 },
            height: { xs: "74vh", sm: 600 },
            borderRadius: 4,
            background: `radial-gradient(60% 60% at 20% 100%, ${accentColor}33 0%, transparent 70%)`,
            filter: "blur(20px)",
            opacity: open ? 1 : 0,
            transition: `opacity ${open ? OPEN_MS : CLOSE_MS}ms ease`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Chat Window */}
      {mounted && (
        <Paper
          elevation={0}
          sx={{
            width: { xs: "92vw", sm: 420 },
            height: { xs: "74vh", sm: 600 },
            maxHeight: "84vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            overflow: "hidden",
            mb: 2,
            position: "absolute",
            bottom: 76,
            right: 0,
            zIndex: 1,
            transformOrigin: "bottom left",
            boxShadow: "0 24px 48px -12px rgba(15,23,42,0.28), 0 8px 20px -4px rgba(15,23,42,0.12)",
            border: "1px solid rgba(15,23,42,0.06)",
            clipPath: open ? "circle(150% at 0% 100%)" : "circle(0% at 0% 100%)",
            transform: open ? "translateY(0) scale(1)" : "translateY(10px) scale(0.98)",
            transition: open
              ? `clip-path ${OPEN_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${OPEN_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
              : `clip-path ${CLOSE_MS}ms cubic-bezier(0.7, 0, 0.84, 0), transform ${CLOSE_MS}ms ease-in`,
            pointerEvents: open ? "auto" : "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
              color: "#fff",
              flexShrink: 0,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-6px)",
              transition: `opacity 240ms ease ${open ? 120 : 0}ms, transform 240ms ease ${open ? 120 : 0}ms`,
            }}
          >
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.25)" }}>
              <SupportAgentRoundedIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap letterSpacing={0.2}>
                {hotelName}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#22c55e",
                    boxShadow: "0 0 0 3px rgba(34,197,94,0.25)",
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                   Online
                </Typography>
              </Stack>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                color: "#fff",
                transition: "background-color 150ms ease, transform 150ms ease",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)", transform: "rotate(90deg)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              bgcolor: "#f4f6fb",
              scrollBehavior: "smooth",
              opacity: open ? 1 : 0,
              transition: `opacity 260ms ease ${open ? 180 : 0}ms`,
            }}
          >
            {messages.map((m) =>
              m.kind === "dateForm" ? (
                <AnimatedEntry key={m.id}>
                  <DateRangeFormMessage accentColor={accentColor} onSearch={handleReservationSearch} />
                </AnimatedEntry>
              ) : m.kind === "reportMenu" ? (
                <AnimatedEntry key={m.id}>
                  <ReportMenuMessage accentColor={accentColor} onPick={runAction} />
                </AnimatedEntry>
              ) : m.kind === "reportDateForm" ? (
                <AnimatedEntry key={m.id}>
                  <ReportDateFormMessage cfg={REPORTS[m.reportKey]} onApply={(params) => loadReport(m.reportKey, params)} />
                </AnimatedEntry>
              ) : m.kind === "reservationList" ? (
                <AnimatedEntry key={m.id}>
                  <ReservationListMessage
                    msg={m}
                    accentColor={accentColor}
                    expandedId={expandedId}
                    setExpandedId={setExpandedId}
                    onLoadMore={handleLoadMore}
                    loadingMore={loading}
                  />
                </AnimatedEntry>
              ) : (
                <MessageBubble key={m.id} message={m} accentColor={accentColor} />
              )
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 0.5,
                  animation: "chatbot-fade-in 200ms ease-out",
                  "@keyframes chatbot-fade-in": {
                    from: { opacity: 0, transform: "translateY(4px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                <Avatar sx={{ width: 26, height: 26, bgcolor: accentColor }}>
                  <SmartToyRoundedIcon sx={{ fontSize: 15 }} />
                </Avatar>
                <Box sx={{ bgcolor: "#fff", borderRadius: "14px 14px 14px 4px", px: 1.75, py: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                  <TypingDots />
                </Box>
              </Box>
            )}

            {/* Quick-action chips — always visible, same style as the guest bot */}
            {!loading && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, fontWeight: 600 }}>
                  Quick actions
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
                  {QUICK_ACTIONS.map((qa) => (
                    <Chip
                      key={qa.label}
                      label={qa.label}
                      size="small"
                      onClick={() => handleChipClick(qa)}
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #e2e5ec",
                        fontWeight: 500,
                        transition: "all 150ms ease",
                        "&:hover": { bgcolor: "#eef2ff", borderColor: accentColor, transform: "translateY(-1px)" },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 1.5,
              borderTop: "1px solid #eee",
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              bgcolor: "#fff",
              flexShrink: 0,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(6px)",
              transition: `opacity 260ms ease ${open ? 220 : 0}ms, transform 260ms ease ${open ? 220 : 0}ms`,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#f5f6fa",
                  transition: "background-color 150ms ease",
                  "&.Mui-focused": { bgcolor: "#fff" },
                },
              }}
            />
            <Tooltip title="Send">
              <span>
                <IconButton
                  onClick={handleTypedSend}
                  disabled={loading || !input.trim()}
                  sx={{
                    bgcolor: accentColor,
                    color: "#fff",
                    transition: "all 150ms ease",
                    "&:hover": { bgcolor: accentColor, opacity: 0.9, transform: "scale(1.05)" },
                    "&.Mui-disabled": { bgcolor: "#e2e5ec", color: "#aaa" },
                  }}
                >
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {/* Launcher Button — same bell-icon / pulse-ring / swap animation as the guest bot */}
      <Tooltip title={open ? "" : "Manager chat"} placement="right">
        <Box sx={{ position: "relative", width: 62, height: 62, zIndex: 2 }}>
          {!open && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                bgcolor: accentColor,
                opacity: 0.35,
                animation: "chatbot-pulse 2.4s ease-out infinite",
                "@keyframes chatbot-pulse": {
                  "0%": { transform: "scale(1)", opacity: 0.35 },
                  "70%": { transform: "scale(1.45)", opacity: 0 },
                  "100%": { transform: "scale(1.45)", opacity: 0 },
                },
              }}
            />
          )}
          <IconButton
            onClick={() => setOpen((o) => !o)}
            sx={{
              position: "relative",
              width: 62,
              height: 62,
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
              color: "#fff",
              boxShadow: open ? "0 8px 20px rgba(37,99,235,0.28)" : "0 10px 28px rgba(37,99,235,0.4)",
              transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 220ms ease",
              "&:hover": {
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                transform: "scale(1.06)",
              },
              "&:active": { transform: "scale(0.9)" },
            }}
          >
            <Box sx={{ position: "relative", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {/* Concierge bell icon (closed state) */}
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  transition: "opacity 180ms ease, transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                  opacity: open ? 0 : 1,
                  transform: open ? "rotate(-90deg) scale(0.5)" : "rotate(0deg) scale(1)",
                }}
              >
                <SvgIcon viewBox="55 0 410 500" sx={{ fontSize: 30 }}>
                  <path d="M 245 10 L 230 13 L 209 22 L 197 31 L 188 41 L 178 59 L 174 72 L 172 83 L 172 111 L 177 136 L 183 153 L 191 169 L 203 186 L 215 198 L 235 210 L 249 214 L 265 214 L 279 210 L 297 199 L 311 185 L 321 171 L 330 154 L 337 134 L 342 107 L 342 87 L 339 70 L 331 50 L 322 37 L 310 26 L 292 16 L 275 11 Z" />
                  <path d="M 145 60 L 129 68 L 103 86 L 66 122 L 40 160 L 27 187 L 19 210 L 10 257 L 9 286 L 11 312 L 15 334 L 23 362 L 32 384 L 42 402 L 51 330 L 51 321 L 47 301 L 46 273 L 49 244 L 55 219 L 63 197 L 73 177 L 89 153 L 118 122 L 141 105 L 141 81 Z" />
                  <path d="M 368 61 L 372 76 L 373 106 L 394 122 L 414 142 L 426 157 L 440 179 L 457 219 L 465 259 L 466 283 L 463 318 L 472 399 L 491 356 L 501 311 L 503 282 L 502 258 L 493 210 L 482 180 L 472 160 L 446 122 L 409 86 L 383 68 Z" />
                  <path d="M 87 290 L 68 453 L 68 472 L 75 479 L 106 488 L 153 494 L 297 497 L 409 487 L 435 480 L 446 472 L 431 301 L 424 282 L 411 270 L 338 249 L 286 396 L 280 325 L 259 297 L 278 278 L 283 265 L 266 245 L 259 242 L 249 245 L 232 265 L 234 274 L 255 297 L 234 322 L 228 397 L 176 250 L 112 268 L 99 276 Z" />
                  <g transform="translate(328 10) scale(1.15)">
                    <path
                      d="M40 20h100c11 0 20 9 20 20v58c0 11-9 20-20 20H92l-30 25v-25H40c-11 0-20-9-20-20V40c0-11 9-20 20-20Z"
                      fill="currentColor"
                    />
                    <circle cx="65" cy="69" r="6" fill="white" />
                    <circle cx="90" cy="69" r="6" fill="white" />
                    <circle cx="115" cy="69" r="6" fill="white" />
                  </g>
                </SvgIcon>
              </Box>
              {/* Close icon (open state) */}
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  transition: "opacity 180ms ease, transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                  opacity: open ? 1 : 0,
                  transform: open ? "rotate(0deg) scale(1)" : "rotate(90deg) scale(0.5)",
                }}
              >
                <CloseRoundedIcon />
              </Box>
            </Box>
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Shared entrance animation wrapper — same keyframe the guest bot uses
// for every message bubble, reused here for the date form and the
// reservation list so everything entering the thread animates the
// same way.
// ---------------------------------------------------------------------
function AnimatedEntry({ children }) {
  return (
    <Box
      sx={{
        animation: "chatbot-msg-in 240ms cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes chatbot-msg-in": {
          from: { opacity: 0, transform: "translateY(6px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Report menu — list of reports after "Report" is clicked
// ---------------------------------------------------------------------
function ReportMenuMessage({ onPick, accentColor }) {
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 16px 4px", p: 1.75, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", maxWidth: "92%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Which report would you like?</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {REPORT_OPTIONS.map((r) => (
          <Chip
            key={r.action}
            label={r.label}
            size="small"
            onClick={() => onPick(r.action)}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #e2e5ec",
              fontWeight: 500,
              transition: "all 150ms ease",
              "&:hover": { bgcolor: "#eef2ff", borderColor: accentColor, transform: "translateY(-1px)" },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Generic report date form — driven by REPORTS[x].dateMode ("single" | "range").
// Stays editable so the manager can re-apply another date.
// ---------------------------------------------------------------------
function ReportDateFormMessage({ cfg, onApply }) {
  const range = cfg?.dateMode === "range";
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(range ? addDaysISO(7) : todayISO());
  const invalid = !start || (range && (!end || end < start));

  const apply = () => {
    if (invalid) return;
    onApply(range ? { startDate: start, endDate: end } : { date: start });
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 16px 4px", p: 1.75, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", maxWidth: "92%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
        {cfg?.label} — select {range ? "a date range" : "a date"}
      </Typography>
      <Stack direction="row" gap={1}>
        <TextField type="date" size="small" label={range ? "From" : "Date"} value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        {range && (
          <TextField type="date" size="small" label="To" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        )}
      </Stack>
      <Button fullWidth variant="contained" disableElevation sx={{ mt: 1.25 }} onClick={apply} disabled={invalid}>
        Apply
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Binds the reusable ReportDialog to a registry entry (body + optional print).
// ---------------------------------------------------------------------
function ReportDialogHost({ state, printRef, onClose, onRetry, primaryColor, accentColor }) {
  const cfg = state.key ? REPORTS[state.key] : null;
  if (!cfg) return null;
  const { View, Print } = cfg;
  const ready = cfg.selfLoading || state.data;

  return (
    <ReportDialog
      open={state.open}
      onClose={onClose}
      title={cfg.label}
      subtitle={reportSubtitle(cfg, state.params)}
      loading={state.loading}
      error={state.error}
      onRetry={onRetry}
      primaryColor={primaryColor}
      accentColor={accentColor}
      actions={
        Print && state.data ? (
          <ReactToPrint
            trigger={() => (
              <Button size="small" variant="outlined" sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", mr: 0.5 }}>
                Print
              </Button>
            )}
            content={() => printRef.current}
            documentTitle={cfg.printTitle?.(state.params)}
            pageStyle={`@page { size: A4; margin: 11mm 8mm 17mm 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }`}
          />
        ) : null
      }
    >
      {ready && <View params={state.params} data={state.data} />}
      {Print && state.data && (
        <div style={{ display: "none" }}>
          <Print ref={printRef} reportData={state.data} date={cfg.printDate?.(state.params)} t={(s) => s} />
        </div>
      )}
    </ReportDialog>
  );
}

// ---------------------------------------------------------------------
// Reservation search form — dates + search text + "by Booking ID" toggle + status.
// Stays editable so the manager can run several searches.
// ---------------------------------------------------------------------
function DateRangeFormMessage({ onSearch }) {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDaysISO(7));
  const [text, setText] = useState("");
  const [byId, setById] = useState(false);
  const [status, setStatus] = useState(0);

  const invalid = !start || !end || end < start;

  const handleSubmit = () => {
    if (invalid) return;
    let searchText = text.trim();
    // booking ids are shown as "#000123" — the API wants the plain id
    if (byId) searchText = searchText.replace(/^#?0*/, "");
    onSearch({ startDate: start, endDate: end, searchText, searchById: byId && !!searchText, bookingStatus: status });
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 16px 4px", p: 1.75, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", maxWidth: "92%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Search reservations by check-in date range</Typography>
      <Stack direction="row" gap={1}>
        <TextField type="date" size="small" label="From" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField type="date" size="small" label="To" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
      </Stack>

      <TextField
        size="small"
        fullWidth
        sx={{ mt: 1.25 }}
        label={byId ? "Booking ID" : "Search"}
        placeholder={byId ? "e.g. 123" : "Guest name, phone..."}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        inputProps={byId ? { inputMode: "numeric" } : undefined}
      />
      <FormControlLabel
        sx={{ mt: 0.25, ml: 0 }}
        control={<Switch size="small" checked={byId} onChange={(e) => setById(e.target.checked)} />}
        label={<Typography sx={{ fontSize: 13 }}>Search by Booking ID</Typography>}
      />

      <TextField select size="small" fullWidth label="Booking Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ mt: 0.5 }}>
        <MenuItem value={0}>All</MenuItem>
        {BOOKING_STATUS_OPTIONS.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>

      <Button fullWidth variant="contained" disableElevation sx={{ mt: 1.25 }} onClick={handleSubmit} disabled={invalid}>
        Search
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Collapsible, paginated reservation list
// ---------------------------------------------------------------------
function ReservationListMessage({ msg, accentColor, expandedId, setExpandedId, onLoadMore, loadingMore }) {
  const hasMore = msg.rows.length < msg.totalRows;
  console.log(msg);
  const statusName = BOOKING_STATUS_OPTIONS.find((s) => s.id === msg.bookingStatus)?.name;
  const filterSummary = [
    statusName && `Status: ${statusName}`,
    msg.searchText && `${msg.searchById ? "Booking ID" : "Search"}: ${msg.searchText}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ maxWidth: "96%" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
        {msg.totalRows} reservation{msg.totalRows === 1 ? "" : "s"} found · {msg.startDate} → {msg.endDate}
      </Typography>
      {filterSummary && (
        <Typography sx={{ fontSize: 12, color: "#64748b", mt: -0.5, mb: 1 }}>{filterSummary}</Typography>
      )}

      <Stack gap={0.75}>
        {msg.rows.map((r) => (
          <Accordion
            key={r.id}
            disableGutters
            expanded={expandedId === r.id}
            onChange={() => setExpandedId(expandedId === r.id ? null : r.id)}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              "&:before": { display: "none" },
              borderRadius: "10px !important",
              overflow: "hidden",
              transition: "box-shadow 150ms ease",
              "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.12)" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {r.bookingId} · {r.name}
                </Typography>
                <Chip label={r.status} size="small" sx={{ bgcolor: accentColor, color: "#fff", height: 20, fontSize: 11 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <DetailRow label="Booking ID" value={r.bookingId} />
              <DetailRow label="Guest Name" value={r.name} />
              {/* <DetailRow label="Phone Number" value={r.phone} /> */}
              {/* <DetailRow label="Room No" value={r.bookingItemsData?.[0]?.room_no} /> */}
              <DetailRow label="Check-In Date" value={r.checkin} />
              <DetailRow label="Check-Out Date" value={r.checkout} />
              <DetailRow label="Booking Status" value={r.status} />
              <DetailRow label="No. Of Days" value={r.nights} />
              <DetailRow label="Final Booking Amount" value={r.amount} />
              <DetailRow label="Room Type" value={r.roomType} last />
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {hasMore && (
        <Button
          fullWidth
          size="small"
          onClick={() => onLoadMore(msg)}
          disabled={loadingMore}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {loadingMore ? "Loading..." : `Load 10 more (${msg.rows.length} of ${msg.totalRows})`}
        </Button>
      )}
    </Box>
  );
}

function DetailRow({ label, value, last }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.6, borderBottom: last ? "none" : "1px solid #f0f1f4" }}>
      <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Message bubble + structured-text formatting — identical to the guest
// bot's version, duplicated here (not imported) to keep the two bots
// fully independent files.
// ---------------------------------------------------------------------
function MessageBubble({ message, accentColor }) {
  const isUser = message.role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        gap: 1,
        animation: "chatbot-msg-in 240ms cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes chatbot-msg-in": {
          from: { opacity: 0, transform: "translateY(6px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
      }}
    >
      {!isUser && (
        <Avatar sx={{ width: 26, height: 26, bgcolor: accentColor, mt: 0.3, flexShrink: 0 }}>
          <SmartToyRoundedIcon sx={{ fontSize: 15 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: "82%" }}>
        <Box
          sx={{
            bgcolor: isUser ? accentColor : "#fff",
            color: isUser ? "#fff" : "#1e293b",
            px: 1.75,
            py: isUser ? 1 : 1.25,
            borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          {isUser ? (
            <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message.text}</Box>
          ) : (
            <FormattedBotText text={message.text} />
          )}
        </Box>

        {!isUser && message.link && (
          <Box sx={{ mt: 0.75 }}>
            <Chip
              component="a"
              href={message.link.url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              size="small"
              icon={<RoomRoundedIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
              label={message.link.label}
              sx={{
                bgcolor: accentColor,
                color: "#fff",
                fontWeight: 500,
                transition: "all 150ms ease",
                "&:hover": { bgcolor: accentColor, opacity: 0.9, transform: "translateY(-1px)" },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Renders a bot reply as plain text, or — if it follows the
 * "intro line\n• Label: value\n..." shape — as a heading + bullet list.
 * buildHotelInfoMessage() in chatbotApi.js returns exactly that shape.
 */
function FormattedBotText({ text }) {
  if (!text) return null;

  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  const bulletLines = lines.filter((l) => l.trim().startsWith("•"));

  if (bulletLines.length === 0) {
    return <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</Box>;
  }

  const introLines = lines.filter((l) => !l.trim().startsWith("•"));

  const rows = bulletLines.map((l) => {
    const stripped = l.trim().replace(/^•\s*/, "");
    const colonIdx = stripped.indexOf(":");
    if (colonIdx > -1 && colonIdx < 40) {
      return { label: stripped.slice(0, colonIdx).trim(), value: stripped.slice(colonIdx + 1).trim() };
    }
    return { label: null, value: stripped };
  });

  return (
    <Box>
      {introLines.length > 0 && (
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#0f172a", mb: 0.75, lineHeight: 1.45 }}>
          {introLines.join(" ")}
        </Typography>
      )}
      <Stack gap={0.6}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.9 }}>
            <FiberManualRecordRoundedIcon sx={{ fontSize: 6, color: "#94a3b8", mt: 0.9, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: "#334155", wordBreak: "break-word" }}>
              {row.label ? (
                <>
                  <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {row.label}:
                  </Box>{" "}
                  {row.value}
                </>
              ) : (
                row.value
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function TypingDots() {
  return (
    <Stack direction="row" gap={0.5} sx={{ py: 0.25 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "#94a3b8",
            animation: "chatbot-bounce 1.2s infinite ease-in-out",
            animationDelay: `${i * 0.15}s`,
            "@keyframes chatbot-bounce": {
              "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
              "40%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        />
      ))}
    </Stack>
  );
}